import React, { useEffect, useState, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import api from "../services/api";

export default function Ticket({ invoice, onAfterPrint }) {
  const [company, setCompany] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);
  const ticketRef = useRef(null);

  // ── useEffect 1: cargar datos de empresa ──
  useEffect(() => {
    api.get('/company/info')
      .then(r => {
        setCompany(r.data);
        setIsReady(true);
      })
      .catch(err => {
        setError(err.message);
        setCompany({
          nombre: "RESTAURANTE DEMO",
          direccion: "Dirección no disponible",
          telefono: "0000-0000",
          email: "info@restaurante.com",
          nit: "0000-000000-000-0",
          nrc: "000000-0"
        });
        setIsReady(true);
      });
  }, []);

  // ── useEffect 2: imprimir cuando llega la factura ──
  useEffect(() => {
    if (!invoice || !isReady) return;

    const timer = setTimeout(async () => {
      try {
        const saleId = invoice.id;
        const token = localStorage.getItem('token');

        // El back genera el PDF y lo devuelve como blob
        const url = `https://restaurante.altivaweb.com/BACK/public/api/print/ticket/${saleId}`;

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });

        if (!response.ok) throw new Error('Error al generar ticket PDF');

        const blob = await response.blob();
        const pdfUrl = URL.createObjectURL(blob);

        // Imprimir usando iframe oculto sin abrir ventana nueva
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = pdfUrl;
        document.body.appendChild(iframe);
        iframe.onload = () => {
          iframe.contentWindow.print();
          setTimeout(() => {
            document.body.removeChild(iframe);
            URL.revokeObjectURL(pdfUrl);
          }, 2000);
        };

      } catch (err) {
        console.error('Error generando ticket PDF:', err);
      }

      if (onAfterPrint) onAfterPrint();
    }, 1500);

    return () => clearTimeout(timer);
  }, [invoice, isReady, company, onAfterPrint]);

  if (!invoice || !isReady) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: '18px' }}>
      Cargando...
    </div>
  );

  const sale = invoice;
  const fel = invoice.invoice;
  const client = invoice.client ?? {};
  const details = invoice.sale_details ?? invoice.saleDetails ?? [];
  const efectivo = invoice.cash ?? sale.cash ?? sale.efectivo ?? 0;
  const cambio = invoice.change ?? sale.change ?? sale.cambio ?? 0;
  const numeroControl = fel?.numero_control;
  const tipoDocumento = fel?.tipo_documento || sale?.tipo_documento || "01";
  const esCreditoFiscal = tipoDocumento === "03";
  const esConsumidorFinal = tipoDocumento === "01";

  const obtenerTituloDocumento = () => {
    if (esCreditoFiscal) return "CRÉDITO FISCAL";
    if (esConsumidorFinal) return "CONSUMIDOR FINAL";
    return "FACTURA";
  };

  const obtenerNombreTipoDocumento = (codigo) => {
    const tipos = { '36': 'NIT', '13': 'DUI', '37': 'Otro', '03': 'Pasaporte', '02': 'Carnet de Residente' };
    return tipos[codigo] || 'Documento';
  };

  return (
    <>
      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { background: #f7fafc; }
        .ticket-wrapper { display: flex; justify-content: center; align-items: center; min-height: 100vh; padding: 20px; }
        .ticket { width: 80mm; font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #000; background: white; padding: 8mm; box-shadow: 0 4px 12px rgba(0,0,0,0.15); margin: 0 auto; }
        .ticket-center { text-align: center; }
        .ticket-company { font-weight: bold; font-size: 14px; margin-bottom: 6px; }
        .ticket-info { font-size: 10px; line-height: 1.4; }
        .ticket-info-line { margin: 2px 0; }
        .ticket-divider { border: none; border-top: 1px dashed #000; margin: 10px 0; }
        .ticket-section-title { font-weight: bold; font-size: 13px; text-align: center; margin: 10px 0 6px 0; }
        .ticket-row { display: flex; justify-content: space-between; margin: 4px 0; font-size: 11px; }
        .ticket-row-bold { font-weight: bold; }
        .ticket-table-header { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; font-weight: bold; border-bottom: 1px solid #000; padding-bottom: 4px; margin-bottom: 6px; font-size: 10px; }
        .ticket-table-row { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; margin: 4px 0; font-size: 10px; }
        .ticket-table-cell { text-align: right; }
        .ticket-table-cell:first-child { text-align: left; }
        .ticket-total { font-size: 15px; font-weight: bold; margin: 10px 0; }
        .ticket-qr { text-align: center; margin: 12px 0; }
        .ticket-footer { text-align: center; margin-top: 12px; }
        .text-small { font-size: 10px; }
        .text-bold { font-weight: bold; }
        .error-badge { background-color: #fee; border: 1px solid #fcc; padding: 8px; margin-bottom: 10px; border-radius: 4px; font-size: 10px; color: #c00; }
        @media print {
          * { visibility: hidden; }
          .ticket-wrapper, .ticket-wrapper * { visibility: visible; }
          html, body { margin: 0 !important; padding: 0 !important; background: white !important; }
          .ticket-wrapper { position: absolute; left: 0; top: 0; width: 80mm; padding: 0; margin: 0; }
          .ticket { box-shadow: none; margin: 0; padding: 5mm; width: 80mm; }
          .error-badge { display: none; }
        }
      `}</style>

      <div className="ticket-wrapper">
        <div className="ticket" ref={ticketRef}>

          {error && (
            <div className="error-badge">⚠️ No se pudieron cargar los datos de la empresa.</div>
          )}

          <div className="ticket-center">
            <div className="ticket-section-title" style={{ marginTop: 0 }}>EMISOR</div>
            <div className="ticket-company">{company?.nombre_comercial || company?.nombre || 'RESTAURANTE'}</div>
            <div className="ticket-info">
              <div className="ticket-info-line">{company?.nombre || ''}</div>
              <div className="ticket-info-line"><strong>NIT:</strong> {company?.nit || 'N/A'}</div>
              <div className="ticket-info-line"><strong>NRC:</strong> {company?.nrc || 'N/A'}</div>
              {company?.desc_actividad && <div className="ticket-info-line"><strong>Actividad:</strong> {company.desc_actividad}</div>}
              <div className="ticket-info-line"><strong>Dirección:</strong> {company?.direccion || 'N/A'}</div>
              <div className="ticket-info-line"><strong>Tel:</strong> {company?.telefono || 'N/A'}</div>
              <div className="ticket-info-line"><strong>Email:</strong> {company?.email || 'N/A'}</div>
            </div>
          </div>

          <hr className="ticket-divider" />
          <div className="ticket-section-title">DOCUMENTO TRIBUTARIO ELECTRÓNICO</div>
          <div className="ticket-center">
            <div className="text-bold" style={{ fontSize: '14px' }}>{obtenerTituloDocumento()}</div>
          </div>

          <hr className="ticket-divider" />
          <div className="text-small">
            <div><strong>Número Control:</strong></div>
            <div style={{ wordBreak: 'break-all', marginBottom: '4px' }}>{numeroControl || 'N/A'}</div>
            <div><strong>Código Generación:</strong></div>
            <div style={{ wordBreak: 'break-all', marginBottom: '4px' }}>{fel?.codigo_generacion || 'N/A'}</div>
            {fel?.sello_recepcion && (
              <>
                <div><strong>Sello de Recepción:</strong></div>
                <div style={{ wordBreak: 'break-all', marginBottom: '4px' }}>{fel.sello_recepcion}</div>
              </>
            )}
            <div><strong>Fecha:</strong> {fel?.fecha_emision || new Date().toLocaleDateString()}</div>
          </div>

          <hr className="ticket-divider" />
          <div className="ticket-row"><span><strong>Cajero:</strong></span><span>{sale?.cajero ?? "Sistema"}</span></div>
          <div className="ticket-row"><span><strong>Mesa:</strong></span><span>{sale?.mesa ?? sale?.table ?? "N/A"}</span></div>

          <hr className="ticket-divider" />
          <div className="text-bold" style={{ marginBottom: '4px' }}>CLIENTE</div>
          {esConsumidorFinal ? (
            <div>{client?.nombre || 'clientes varios'}</div>
          ) : (
            <div>
              <div><strong>Nombre:</strong> {client?.nombre || 'Cliente General'}</div>
              {(client?.numero_documento || client?.nit) && (
                <div><strong>{obtenerNombreTipoDocumento(client.tipo_documento)}:</strong> {client.numero_documento || client.nit}</div>
              )}
              {client?.nrc && <div><strong>NRC:</strong> {client.nrc}</div>}
              {(client?.desc_actividad || client?.giro) && <div><strong>Giro:</strong> {client.desc_actividad || client.giro}</div>}
            </div>
          )}

          <hr className="ticket-divider" />
          <div className="ticket-table-header">
            <div>Descripción</div>
            <div className="ticket-table-cell">Cant.</div>
            <div className="ticket-table-cell">P.U.</div>
            <div className="ticket-table-cell">Total</div>
          </div>
          {details.map((item, i) => (
            <div key={i} className="ticket-table-row">
              <div>{item.nombre_producto_snapshot ?? item.product?.nombre ?? "Producto"}</div>
              <div className="ticket-table-cell">{item.cantidad}</div>
              <div className="ticket-table-cell">${Number(item.precio_unitario_snapshot ?? 0).toFixed(2)}</div>
              <div className="ticket-table-cell">${(Number(item.cantidad) * Number(item.precio_unitario_snapshot ?? 0)).toFixed(2)}</div>
            </div>
          ))}

          <hr className="ticket-divider" />
          <div className="ticket-row ticket-total">
            <span>TOTAL:</span>
            <span>${Number(sale?.monto_final ?? 0).toFixed(2)}</span>
          </div>
          {sale?.monto_letras && (
            <div className="ticket-center text-small" style={{ marginTop: '6px' }}>{sale.monto_letras}</div>
          )}

          <hr className="ticket-divider" />
          <div className="ticket-row"><span>Efectivo Recibido:</span><span>${Number(efectivo).toFixed(2)}</span></div>
          <div className="ticket-row ticket-row-bold"><span>Cambio:</span><span>${Number(cambio).toFixed(2)}</span></div>

          <hr className="ticket-divider" />
          {fel?.codigo_generacion && (
            <div className="ticket-qr">
              <QRCodeCanvas
                value={`${"https://restaurante.altivaweb.com/BACK/public"}/factura/${fel.codigo_generacion}`}
                size={140}
                level="M"
                includeMargin={true}
              />
              <div className="text-small" style={{ marginTop: '8px' }}>Escanee para descargar PDF y JSON</div>
            </div>
          )}

          <hr className="ticket-divider" />
          <div className="ticket-footer text-bold" style={{ fontSize: '13px' }}>¡GRACIAS POR SU VISITA!</div>
          <div className="ticket-footer text-small" style={{ marginTop: '6px' }}>Conserve este documento para cualquier reclamo</div>

        </div>
      </div>
    </>
  );
}